import {
  hasHistoryFilters,
  recordLabel,
  type PimsHistoryFilters as Filters,
  type PimsHistoryStatus,
  type PimsRecordRef,
} from "@pims/pimsActivityClient";
import {
  Button,
  Input,
  PaperPanel,
  Select,
  Toggle,
} from "@scribenote/brian-design-system";
import { useId, useState } from "react";

/**
 * Audit filters for the settled-history view (WEB-3250): writes-only toggle, terminal
 * status, a From/To range, and a record selector. Every control maps to a server-side
 * query param (`kind=write`, `status`, `since`/`before`, `record_uuid`), so the page
 * holds only matching rows. State lives in the parent; this is presentational.
 *
 * `records` is the caller-accumulated set of every unique non-null record seen across
 * history pages this session, merged rather than replaced — so filtering to one record
 * can't collapse the dropdown to that option, and an empty page can't blank the
 * selected option's label.
 */
export const PimsHistoryFilters = ({
  filters,
  onChange,
  records,
}: {
  filters: Filters;
  onChange: (filters: Filters) => void;
  records: PimsRecordRef[];
}) => {
  const writesOnlyId = useId();
  const set = (patch: Partial<Filters>) => onChange({ ...filters, ...patch });

  // A second, component-local safety net on top of the caller's own
  // accumulation contract (see the docblock above): remember every record
  // this component has ever been shown, keyed by uuid, so the selected
  // option's label can't blank out even if a caller ever violates that
  // contract and hands back a shrunk (or momentarily empty) `records` prop.
  // Adjusted directly during render (React's endorsed pattern for state
  // derived from a changed prop: https://react.dev/learn/you-might-not-need-an-effect)
  // rather than in a `useEffect`, so there's no extra commit-then-update
  // cascade — the merged set is ready in the very render that received the
  // new `records`.
  const [prevRecords, setPrevRecords] = useState(records);
  const [everSeen, setEverSeen] = useState<Map<string, PimsRecordRef>>(
    () => new Map(records.map((r) => [r.uuid, r])),
  );
  if (records !== prevRecords) {
    setPrevRecords(records);
    if (records.length > 0) {
      const next = new Map(everSeen);
      for (const r of records) next.set(r.uuid, r);
      setEverSeen(next);
    }
  }

  // The dropdown value is the record's `uuid` — what the backend's
  // `record_uuid` query param takes — not the `${kind}:${uuid}` grouping key
  // used elsewhere; the filter narrows by uuid regardless of kind.
  const optionRecords = new Map(everSeen);
  for (const r of records) optionRecords.set(r.uuid, r);
  const recordOptions = [...optionRecords.values()].map((r) => ({
    label: recordLabel(r),
    value: r.uuid,
  }));

  return (
    <PaperPanel
      variant="recessed"
      className="flex items-end gap-3 overflow-x-auto p-3"
    >
      <div className="w-40 shrink-0">
        <Input
          label="From"
          type="date"
          value={sinceToInput(filters.since)}
          max={beforeToInput(filters.before) || undefined}
          onChange={(e) => set({ since: inputToSince(e.target.value) })}
          data-testid="pims-history-since"
        />
      </div>

      <div className="w-40 shrink-0">
        <Input
          label="To"
          type="date"
          value={beforeToInput(filters.before)}
          min={sinceToInput(filters.since) || undefined}
          onChange={(e) => set({ before: inputToBefore(e.target.value) })}
          data-testid="pims-history-before"
        />
      </div>

      {/* The wrappers are widths only: `className` on a Select addresses its
          trigger box, so the field's own track width cannot ride the component. */}
      <div className="w-44 shrink-0">
        <Select
          data-testid="pims-history-status"
          label="Status"
          options={STATUS_OPTIONS}
          value={filters.status ?? ""}
          onValueChange={(value) =>
            set({ status: value ? (value as PimsHistoryStatus) : undefined })
          }
        />
      </div>

      <div className="w-48 shrink-0">
        <Select
          data-testid="pims-history-record"
          label="Record"
          options={[{ label: "Any record", value: "" }, ...recordOptions]}
          value={filters.recordUuid ?? ""}
          onValueChange={(value) =>
            set({ recordUuid: value ? value : undefined })
          }
        />
      </div>

      <div
        className="flex shrink-0 flex-col gap-1.5"
        data-testid="pims-history-writes-only"
      >
        <label
          htmlFor={writesOnlyId}
          className="font-body text-sm font-medium text-ink"
        >
          Writes only
        </label>
        <div className="flex h-9 items-center">
          <Toggle
            id={writesOnlyId}
            checked={Boolean(filters.writesOnly)}
            onCheckedChange={(checked) =>
              set({ writesOnly: checked || undefined })
            }
          />
        </div>
      </div>

      {hasHistoryFilters(filters) && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange({})}
          data-testid="pims-history-clear"
        >
          Clear
        </Button>
      )}
    </PaperPanel>
  );
};

const STATUS_OPTIONS = [
  { label: "Any status", value: "" },
  { label: "Done", value: "done" },
  { label: "Failed", value: "failed" },
  { label: "Timed out", value: "timeout" },
  { label: "Needs clarification", value: "needs_clarification" },
  { label: "Cancelled", value: "cancelled" },
];

/** YYYY-MM-DD in the viewer's local zone for a date input. */
const localDate = (d: Date): string => {
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

// `since` is the inclusive start: local start-of-day ⇄ the picked date.
const sinceToInput = (iso?: string): string =>
  iso ? localDate(new Date(iso)) : "";
const inputToSince = (value: string): string | undefined =>
  value ? new Date(`${value}T00:00:00`).toISOString() : undefined;

// `before` is exclusive (backend semantics), so the picked "To" date is the day
// before it — start-of-next-day makes the picked day fully inclusive.
const beforeToInput = (iso?: string): string =>
  iso ? localDate(new Date(new Date(iso).getTime() - 1)) : "";
const inputToBefore = (value: string): string | undefined => {
  if (!value) return undefined;
  const end = new Date(`${value}T00:00:00`);
  end.setDate(end.getDate() + 1);
  return end.toISOString();
};
